import type { Placement } from '@popperjs/core'
import { createPopper } from '@popperjs/core'
import type {
  Dispatch,
  MutableRefObject,
  PropsWithChildren,
  RefObject,
} from 'react'
import React, {
  createContext,
  createRef,
  forwardRef,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react'
import { useDisposables } from '../hooks/useDisposables'
import { useId } from '../hooks/useId'
import { useInitialRender } from '../hooks/useInitialRender'
import { applyMaxSize, maxSize } from '../utilities/applyMaxSize'
import { calculateActiveIndex } from '../utilities/calculateActiveIndex'
import type { Actions } from './Actions'
import { ActionTypes } from './Actions'
import type { ButtonProps } from './Button'
import Button from './Button'
import Group from './Group'
import type { ItemProps, MenuItemDataRef, SubmenuActions } from './Item'
import Item, { SubmenuActionTypes, useSubmenu } from './Item'
import Items from './Items'

interface MenuProps {
  focusRef?: MutableRefObject<Pick<HTMLOrSVGElement, 'focus'>>
  headless?: boolean
  onClose?: () => void
  onOpen?: () => void
  placement?: Placement
}

type MenuReducers = {
  [ActionType in ActionTypes]: (state: MenuState, action: Actions) => MenuState
}

interface MenuState {
  activeItemIndex: number | null
  buttonRef: MutableRefObject<
    HTMLButtonElement | HTMLDivElement | HTMLLabelElement
  >
  focusRef?: MutableRefObject<HTMLElement>
  headless: boolean
  itemsRef: MutableRefObject<HTMLUListElement>
  items: { id: string; dataRef: MenuItemDataRef }[]
  id: string
  integratedMultiSelect: boolean
  listbox: boolean
  minWidth: string
  onCloseRef: RefObject<() => void | undefined>
  onOpenRef: RefObject<() => void | undefined>
  parentItemRef: MutableRefObject<HTMLButtonElement>
  parentRef: MutableRefObject<HTMLUListElement>
  placement: Placement
  search: string
  searchable: boolean
  selectedItem: { id: string | null; index: number | null }
  show: boolean
  notifyAction?: SubmenuActions
  openSubmenuItemId: string | null
}

type UseMenuValues = [MenuState, Dispatch<Actions>]

const MenuContext = createContext<UseMenuValues | null>(null)

const reducers: MenuReducers = {
  [ActionTypes.CloseAll]: (state) => {
    // if it's a submenu, propagate the action to the parent menu, otherwise close the menu
    if (state.parentRef) {
      return {
        ...state,
        notifyAction: { type: SubmenuActionTypes.CloseAll },
      }
    }

    return { ...state, show: false, openSubmenuItemId: null }
  },
  [ActionTypes.CloseMenu]: (
    state,
    action: Extract<Actions, { type: ActionTypes.CloseMenu }>,
  ) => {
    return {
      ...state,
      notifyAction: action.external
        ? undefined
        : { type: SubmenuActionTypes.CloseMenu },
      show: false,
      openSubmenuItemId: null,
    }
  },
  [ActionTypes.CloseSubmenu]: (state) => {
    return { ...state, openSubmenuItemId: null }
  },
  [ActionTypes.OpenMenu]: (state) => {
    // vertical offset for submenu so that it's centered at its parent item
    const skidding =
      state.parentRef?.current && state.parentItemRef?.current
        ? state.parentItemRef.current.offsetTop -
          state.parentRef.current.offsetHeight / 2 +
          state.parentItemRef.current.offsetHeight / 2 -
          state.parentRef.current.scrollTop
        : 0
    const distance = state.parentRef || state.listbox ? 4 : 8

    createPopper(
      state.parentRef ? state.parentRef.current : state.buttonRef.current,
      state.itemsRef.current,
      {
        modifiers: [
          {
            name: 'flip',
            options: {
              fallbackPlacements: state.parentRef
                ? ['right', 'left']
                : ['top', 'bottom'],
            },
          },
          {
            name: 'offset',
            options: {
              offset: [skidding, distance],
            },
          },
          maxSize,
          applyMaxSize,
        ],
        placement: state.parentRef ? 'right' : state.placement,
      },
    )

    state.onOpenRef.current?.()

    return {
      ...state,
      activeItemIndex: state.listbox ? state.selectedItem.index : null,
      show: true,
    }
  },
  [ActionTypes.OpenSubmenu]: (
    state,
    action: Extract<Actions, { type: ActionTypes.OpenSubmenu }>,
  ) => {
    return { ...state, openSubmenuItemId: action.id }
  },
  [ActionTypes.GoToItem]: (
    state,
    action: Extract<Actions, { type: ActionTypes.GoToItem }>,
  ) => {
    const activeItemIndex = calculateActiveIndex(action, {
      resolveActiveIndex: () => state.activeItemIndex,
      resolveDisabled: (item) => item.dataRef.current.disabled,
      resolveId: (item) => item.id,
      resolveItems: () => state.items,
    })

    return { ...state, activeItemIndex }
  },
  [ActionTypes.SelectItem]: (
    state,
    action: Extract<Actions, { type: ActionTypes.SelectItem }>,
  ) => {
    const index = state.items.findIndex((item) => item.id === action.id)

    return {
      ...state,
      selectedItem:
        index >= 0 ? { id: action.id, index } : { id: null, index: null },
      show: action.keepShow ? state.show : false,
    }
  },
  [ActionTypes.RefreshSelectedIndex]: (state) => {
    if (state.selectedItem.id) {
      const index = state.items.findIndex(
        (item) => item.id === state.selectedItem.id,
      )

      return {
        ...state,
        selectedItem:
          index >= 0
            ? { ...state.selectedItem, index }
            : { id: null, index: null },
      }
    }

    return state
  },
  [ActionTypes.Search]: (
    state,
    action: Extract<Actions, { type: ActionTypes.Search }>,
  ) => {
    const query = state.search + action.value
    const match = state.items.findIndex(
      (item) =>
        item.dataRef.current.textValue?.startsWith(query) &&
        !item.dataRef.current.disabled,
    )

    if (match === -1 || match === state.activeItemIndex) {
      return { ...state, search: query }
    }

    return {
      ...state,
      activeItemIndex: match,
      search: query,
    }
  },
  [ActionTypes.ClearSearch]: (state) => ({ ...state, search: '' }),
  [ActionTypes.RegisterItem]: (
    state,
    action: Extract<Actions, { type: ActionTypes.RegisterItem }>,
  ) => ({
    ...state,
    items: [...state.items, { id: action.id, dataRef: action.dataRef }],
  }),
  [ActionTypes.UnregisterItem]: (
    state,
    action: Extract<Actions, { type: ActionTypes.UnregisterItem }>,
  ) => {
    const nextItems = state.items.slice()
    const currentActiveItem =
      state.activeItemIndex === null ? null : nextItems[state.activeItemIndex]
    const index = nextItems.findIndex((nextItem) => nextItem.id === action.id)

    if (index !== -1) {
      nextItems.splice(index, 1)
    }

    return {
      ...state,
      items: nextItems,
      activeItemIndex: (() => {
        if (index === state.activeItemIndex) {
          return null
        }

        if (currentActiveItem === null) {
          return null
        }

        return nextItems.indexOf(currentActiveItem)
      })(),
    }
  },
  [ActionTypes.UnregisterNotifyAction]: (state) => ({
    ...state,
    notifyAction: undefined,
  }),
  [ActionTypes.RegisterMultiSelect]: (state) => ({
    ...state,
    integratedMultiSelect: true,
    searchable: false,
  }),
  [ActionTypes.RegisterSelect]: (state) => ({
    ...state,
    listbox: true,
  }),
}

const runReducer = (state: MenuState, action: Actions): MenuState =>
  reducers[action.type](state, action)

export const useMenu = (): UseMenuValues =>
  useContext(MenuContext) as UseMenuValues

function Menu({
  children,
  focusRef,
  headless = false,
  onClose,
  onOpen,
  placement = 'bottom-start',
}: PropsWithChildren<MenuProps>): JSX.Element {
  const d = useDisposables()
  const parentState = useMenu()?.[0]
  const { ref, show } = useSubmenu()?.[0] || {}
  const notifyParent = useSubmenu()?.[1]
  const initialRender = useInitialRender()
  const onCloseRef = useRef(onClose)
  const onOpenRef = useRef(onOpen)
  const [state, dispatch] = useReducer(runReducer, {
    activeItemIndex: null,
    buttonRef: parentState ? parentState.itemsRef : createRef(),
    focusRef,
    headless,
    itemsRef: createRef(),
    items: [],
    id: useId(),
    integratedMultiSelect: false,
    placement,
    onCloseRef: onCloseRef,
    onOpenRef: onOpenRef,
    search: '',
    searchable: true,
    listbox: false,
    selectedItem: { id: null, index: null },
    show: false,
    notifyAction: undefined,
    openSubmenuItemId: null,
    parentItemRef: ref,
    parentRef: parentState ? parentState.itemsRef : null,
    minWidth: 'auto',
  } as MenuState)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    onOpenRef.current = onOpen
  }, [onOpen])

  useEffect(() => {
    function clickAway(event: MouseEvent) {
      const target = event.target as HTMLElement

      if (!state.show) {
        return
      }

      if (state.buttonRef.current?.contains(target)) {
        return
      }

      // check if target in inside the portal that contains the main menu and all submenus
      if (
        state.itemsRef.current?.parentElement?.parentElement?.contains(target)
      ) {
        return
      }

      dispatch({ type: ActionTypes.CloseMenu })
    }

    window.addEventListener('mousedown', clickAway)

    return () => window.removeEventListener('mousedown', clickAway)
  }, [d, dispatch, state])

  useEffect(() => {
    notifyParent?.({
      type: SubmenuActionTypes.RegisterMenu,
      id: `coconut-menu-${state.id}`,
    })

    return () => notifyParent?.({ type: SubmenuActionTypes.UnregisterMenu })
  }, [notifyParent, state.id])

  useEffect(() => {
    // when submenu is opened/closed from parent, act as if this menu's button is clicked
    if (show !== undefined && state.show !== show) {
      if (show) {
        dispatch({ type: ActionTypes.OpenMenu })
        if (!state.integratedMultiSelect) {
          d.nextFrame(() => {
            state.itemsRef.current?.focus({ preventScroll: true })
          })
        }
      } else {
        dispatch({ type: ActionTypes.CloseMenu, external: true })
        d.nextFrame(() => {
          state.buttonRef.current?.focus({ preventScroll: true })
        })
      }
    }
    // we don't want to include stat.show because this hook should only trigger when
    // submenu is opened/closed from its parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, dispatch, state.itemsRef, state.buttonRef, d])

  useEffect(() => {
    // when submenu is closed from within, need to notify parent
    if (state.notifyAction) {
      dispatch({ type: ActionTypes.UnregisterNotifyAction })
      notifyParent?.(state.notifyAction)
    }
  }, [notifyParent, state.notifyAction])

  useEffect(() => {
    // when all menus are closed, focus on the outermost menu's focusable element
    if (!state.show && !state.parentRef) {
      d.nextFrame(() => {
        if (!initialRender) {
          if (state.headless) {
            state.buttonRef.current?.parentElement?.parentElement?.focus({
              preventScroll: true,
            })
          } else {
            if (state.focusRef?.current) {
              state.focusRef.current?.focus({ preventScroll: true })
            } else {
              state.buttonRef.current?.focus({ preventScroll: true })
            }
          }
        }
      })
    }
  }, [
    d,
    state.buttonRef,
    state.focusRef,
    state.headless,
    state.parentRef,
    state.show,
  ])

  useEffect(() => {
    if (!state.show && !initialRender) {
      state.onCloseRef.current?.()
    }
  }, [state.onCloseRef, state.show])

  return (
    <MenuContext.Provider value={[state, dispatch]}>
      <div className="relative flex flex-grow font-sans">{children}</div>
    </MenuContext.Provider>
  )
}

Menu.Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  Button,
)
Menu.Group = Group
Menu.Item = forwardRef<HTMLButtonElement, PropsWithChildren<ItemProps>>(Item)
Menu.Items = forwardRef<
  HTMLUListElement,
  PropsWithChildren<Record<string, unknown>>
>(Items)

export default Menu
